<script>
import { VDialog } from 'vuetify/lib'
import { convertToUnit } from 'vuetify/lib/util/helpers'
import { EButton, ESwitch } from '../index'
import VOverlay from 'vuetify/lib/components/VOverlay/index'
import themeable from 'vuetify/lib/mixins/themeable'
import { getSlot } from 'vuetify/lib/util/helpers'

export default {
  name: 'EDialog',
  mixins: [themeable],
  extends: VDialog,
  props: {
    disabled: Boolean,
    fullscreen: Boolean,
    maxWidth: {
      type: [String, Number],
      default: 'none',
    },
    persistent: Boolean,
    transition: {
      type: [String, Boolean],
      default: 'dialog-bottom-transition',
    },
    width: {
      type: [String, Number],
      default: 'auto',
    },

    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    buttonClose: {
      type: Object,
      default: undefined,
    },
    buttonSubmit: {
      type: Object,
      default: undefined,
    },
    buttonDelete: {
      type: Object,
      default: undefined,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      innerOptions: {},
    }
  },
  computed: {
    hasOptions() {
      return Object.keys(this.options).length
    },
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
    classes() {
      return {
        'e-dialog': true,
        'e-dialog--mobile': this.isMobile,
        ...VDialog.options.computed.classes.call(this),
        ...this.rootThemeClasses,
      }
    },

    contentClasses() {
      return {
        'e-dialog__content--mobile': this.isMobile,
        'v-dialog__content': true,
        'v-dialog__content--active': this.isActive,
      }
    },
  },
  mounted() {
    if (Object.keys(this.options).length) {
      Object.keys(this.options).forEach((key) => {
        this.changeOptionValue(false, key)
      })
    }
  },
  watch: {
    isActive(val) {
      if (!val) {
        this.innerOptions = {}
      }
    },
  },
  methods: {
    // overlayable
    createOverlay() {
      const overlay = new VOverlay({
        propsData: {
          absolute: this.absolute,
          value: false,
          color: this.$vuetify.theme.dark ? 'rgba(45,47,49,0.6)' : 'rgba(22,23,24,.6)', // overlay50
          opacity: 1,
        },
      })

      overlay.$mount()

      const parent = this.absolute ? this.$el.parentNode : document.querySelector('[data-app]')

      parent && parent.insertBefore(overlay.$el, parent.firstChild)

      this.overlay = overlay
    },

    onClickCloseButton(e) {
      this.isActive = false
      this.$emit('click:close', e)
    },
    onClickSubmitButton(e) {
      this.$emit('click:submit', e, this.hasOptions ? this.innerOptions : undefined)
    },
    onClickDeleteButton(e) {
      this.$emit('click:delete', e, this.hasOptions ? this.innerOptions : undefined)
    },
    changeOptionValue(val, optionKey) {
      this.innerOptions[optionKey] = val
    },
    getCustomContentSlot() {
      return getSlot(this, 'content', this.getValueProxy(), true)
    },
    getCustomActionsSlot() {
      return getSlot(this, 'actions', this.getValueProxy(), true)
    },
    getOptions() {
      const optionsChildren = Object.entries(this.options).map((opt, idx) => {
        const optKey = opt[0]
        const optValue = opt[1]
        return this.$createElement(
          ESwitch,
          {
            class: idx ? 'mt-1' : '',
            props: {
              label: optValue.label,
              value: this.innerOptions[optKey],
            },
            on: {
              change: (e) => {
                this.changeOptionValue(e, optKey)
              },
            },
          },
          ''
        )
      })
      return this.$createElement('div', { staticClass: 'e-dialog-options' }, optionsChildren)
    },
    genInnerContent() {
      const data = {
        class: this.classes,
        ref: 'dialog',
        directives: [
          {
            name: 'click-outside',
            value: {
              handler: this.onClickOutside,
              closeConditional: this.closeConditional,
              include: this.getOpenDependentElements,
            },
          },
          {
            name: 'show',
            value: this.isActive,
          },
        ],
        style: {
          transformOrigin: this.origin,
        },
      }

      if (!this.fullscreen && !this.isMobile) {
        data.style = {
          ...data.style,
          maxWidth: this.maxWidth === 'none' ? undefined : convertToUnit(this.maxWidth),
          width: this.width === 'auto' ? undefined : convertToUnit(this.width),
        }
      }

      const actionsButtonClose = this.buttonClose
        ? this.$createElement(EButton, {
            props: {
              variant: 'tertiary',
              large: this.isMobile,
              ...this.buttonClose,
            },
            on: {
              click: this.onClickCloseButton,
            },
          })
        : null
      const actionsButtonSubmit = this.buttonSubmit
        ? this.$createElement(EButton, {
            props: {
              variant: 'primary',
              large: this.isMobile,
              ...this.buttonSubmit,
            },
            on: {
              click: this.onClickSubmitButton,
            },
          })
        : null
      const actionsButtonDelete = this.buttonDelete
        ? this.$createElement(EButton, {
            props: {
              variant: 'destroy',
              large: this.isMobile,
              ...this.buttonDelete,
            },
            on: {
              click: this.onClickDeleteButton,
            },
          })
        : null
      const actions =
        actionsButtonClose || actionsButtonDelete || actionsButtonSubmit
          ? this.$createElement('div', { staticClass: 'e-dialog-actions' }, [
              actionsButtonClose,
              actionsButtonDelete,
              actionsButtonSubmit,
            ])
          : null
      const title = this.title
        ? this.$createElement('div', { staticClass: 'e-dialog-body__title' }, [this.title])
        : null
      const description = this.description
        ? this.$createElement('div', { staticClass: 'e-dialog-body__description' }, [this.description])
        : null
      const body =
        this.title || this.description
          ? this.$createElement('div', { staticClass: 'e-dialog-body' }, [title, description])
          : null
      const content = this.$slots.default
        ? this.$createElement('div', { staticClass: 'e-dialog-content' }, this.getContentSlot())
        : null
      const options = this.hasOptions ? this.getOptions() : null
      const scrollable = this.$createElement('div', { staticClass: 'e-dialog-scrollable-area' }, [
        content,
        body,
        options,
      ])
      const inner = this.$createElement(
        'div',
        {
          staticClass: 'e-dialog__inner',
        },
        [
          this.$slots.content ? this.getCustomContentSlot() : scrollable,
          this.$slots.actions ? this.getCustomActionsSlot() : actions,
        ]
      )
      return this.$createElement('div', data, [inner])
    },
  },
}
</script>
